import React, { FC, useEffect, useRef, useState } from 'react';
import { ScrollView, View, Text, StyleProp, ViewStyle } from 'react-native';
import { ColorFunction } from '@styles/colors/types';
import { scale } from '@components/frame';
import { getBoxShadowStyle, ShadowType } from '@styles/shadow';
import TouchableElement from '@components/common/touchableElement';
import { isPhone, isWeb } from '@helpers';
import RadioButton from '@components/common/radioButton';
import { font } from '@styles/fonts';
import Loading, { SmallLoading } from '@components/loading';
import { PADDING_HORIZONTAL_FOR_SHADOW, SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';

export interface RadioButtonWithFetchCallProps<T> {
  // Layout calculation props
  elementsPerRow?: number;
  borderWidth?: number;
  elementsGap?: number;
  paddingHorizontal?: number;

  // Timeout effect props
  timeoutCallback?: (controller: AbortController) => Promise<void> | void;
  timeoutDelay?: number;
  timeoutDependencies?: any[];
  shouldRunTimeout?: boolean;

  // Loading state
  loaded: boolean;
  checkShouldDisplayLoadingForItem: (item: T) => boolean;

  // Content props
  children?: React.ReactNode;

  // Items data and rendering
  items: T[];
  itemKeyExtractor: (item: T, index: number) => string;
  isItemSelected: (item: T) => boolean;
  onItemSelect: (item: T) => void;
  getItemDisplayText: (item: T) => string;

  // Styling
  C: ColorFunction;

  // Style customization
  scrollViewStyle?: object;
  loadingContainerStyle?: object;
  itemHeight?: number;
  itemPaddingHorizontal?: number;
  itemGap?: number;
  itemBorderRadius?: number;
  shadowType?: ShadowType;
}

interface RadioButtonsWithFetchCallItemProps<T> {
  item: T;
  isSelected: boolean;
  C: ColorFunction;
  elementWidth: number;
  borderWidth: number;
  onSelect: (item: T) => void;
  displayText: string;
  height?: number;
  paddingHorizontal?: number;
  gap?: number;
  borderRadius?: number;
  shadowType?: ShadowType;
  isItemLoading?: boolean;
}

const RadioButtonWithFetchCall = <T,>({
  item,
  isSelected,
  C,
  elementWidth,
  borderWidth,
  onSelect,
  displayText,
  height = scale(48),
  paddingHorizontal = scale(12),
  gap = scale(12),
  borderRadius = scale(9),
  shadowType = 'Field',
  isItemLoading = false,
}: RadioButtonsWithFetchCallItemProps<T>) => {
  const shadowStyles = getBoxShadowStyle({ type: shadowType, C });

  return (
    <TouchableElement onPress={() => onSelect(item)}>
      {({ pressHoverColor, boxStyle }) => (
        <View
          style={[
            boxStyle,
            {
              backgroundColor: C(pressHoverColor('Background/Light/Field')),
              width: isPhone ? '100%' : elementWidth,
              height,
              paddingHorizontal,
              flexDirection: 'row',
              alignItems: 'center',
              gap,
              borderRadius,
              borderWidth,
              borderColor: isSelected ? C('Selected/Light/Primary') : 'transparent',
            },
            isWeb && shadowStyles,
          ]}
        >
          {isItemLoading ? (
            <View style={{ width: scale(24), height: scale(24) }}>
              <SmallLoading />
            </View>
          ) : (
            <RadioButton {...{ C, isSelected, onSelect: () => onSelect(item) }} />
          )}

          <View style={{ flex: 1 }}>
            <Text numberOfLines={1} style={[font.Body, { color: C('Label/Light/Primary'), userSelect: 'none' }]}>
              {displayText}
            </Text>
          </View>
        </View>
      )}
    </TouchableElement>
  );
};

const RadioButtonsWithFetchCall = <T,>({
  elementsPerRow = isPhone ? 1 : 3,
  paddingHorizontal = scale(SCREEN_PADDING_HORIZONTAL) - PADDING_HORIZONTAL_FOR_SHADOW,
  borderWidth = scale(3),
  elementsGap = scale(isPhone ? 12 : 32),
  timeoutCallback,
  timeoutDelay = 1000,
  timeoutDependencies = [],
  shouldRunTimeout = true,
  loaded,
  children,
  items = [],
  itemKeyExtractor,
  isItemSelected,
  onItemSelect,
  getItemDisplayText,
  C,
  scrollViewStyle = {},
  loadingContainerStyle = {},
  itemHeight,
  itemPaddingHorizontal,
  itemGap,
  itemBorderRadius,
  shadowType,
  checkShouldDisplayLoadingForItem,
}: RadioButtonWithFetchCallProps<T>) => {
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const isDirty = useRef<boolean>(false);
  const timeoutCallbackRef = useRef(timeoutCallback);
  const shouldRunTimeoutRef = useRef(shouldRunTimeout);
  timeoutCallbackRef.current = timeoutCallback;
  shouldRunTimeoutRef.current = shouldRunTimeout;

  const elementWidth = containerWidth / elementsPerRow - elementsGap - borderWidth / 2;

  const onItemPress = (i: T) => {
    onItemSelect(i);
    isDirty.current = true;
  };

  useEffect(() => {
    if (!shouldRunTimeout || !timeoutCallback || !isDirty.current) return;

    const controller = new AbortController();

    const timeout = setTimeout(async () => {
      await timeoutCallback(controller);
    }, timeoutDelay);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [...timeoutDependencies]);

  useEffect(() => {
    return () => {
      if (isDirty.current && shouldRunTimeoutRef.current && timeoutCallbackRef.current) {
        timeoutCallbackRef.current(new AbortController());
      }
    };
  }, []);

  const onLayout = (event: any) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  };

  const defaultScrollViewStyle: StyleProp<ViewStyle> = {
    marginTop: scale(isPhone ? 12 : 24),
    flexDirection: isPhone ? 'column' : 'row',
    gap: elementsGap,
    alignItems: 'center',
    flexWrap: 'wrap',
    paddingHorizontal: paddingHorizontal,
    ...scrollViewStyle,
  };

  const defaultLoadingStyle: StyleProp<ViewStyle> = {
    width: '100%',
    marginTop: scale(40),
    height: '70%',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    ...loadingContainerStyle,
  };

  const contentReadyToView = loaded && containerWidth > 0;

  return (
    <View style={{ flex: 1 }} onLayout={onLayout}>
      {children}

      {!loaded && (
        <View style={defaultLoadingStyle}>
          <Loading background={'transparent'} />
        </View>
      )}

      {contentReadyToView && (
        <ScrollView contentContainerStyle={defaultScrollViewStyle}>
          {items.map((item, index) => (
            <RadioButtonWithFetchCall
              key={itemKeyExtractor(item, index)}
              isItemLoading={checkShouldDisplayLoadingForItem(item)}
              item={item}
              isSelected={isItemSelected(item)}
              C={C}
              elementWidth={elementWidth}
              borderWidth={borderWidth}
              onSelect={onItemPress}
              displayText={getItemDisplayText(item)}
              height={itemHeight}
              paddingHorizontal={itemPaddingHorizontal}
              gap={itemGap}
              borderRadius={itemBorderRadius}
              shadowType={shadowType}
            />
          ))}
        </ScrollView>
      )}
    </View>
  );
};

export default RadioButtonsWithFetchCall;
